import {
  Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { KpiCardConfig } from '../Services/widget.models';
import { ImageUrlPipe } from "../../../../pipe/image-url.pipe";

@Component({
  selector: 'app-kpi-card',
  standalone: true,
  imports: [CommonModule, ImageUrlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kpi-card" [style.borderColor]="config.color || null">

      <!-- Icon -->
      <div class="kpi-icon-wrap" [ngStyle]="iconWrapStyles">
        <img *ngIf="config.icon; else fallbackIcon"
             [src]="config.icon|imageUrl"
             class="kpi-icon-img"
             aria-hidden="true" />
        <ng-template #fallbackIcon>
          <span class="kpi-icon-fallback" aria-hidden="true">◈</span>
        </ng-template>
      </div>

      <!-- Text -->
      <div class="kpi-text-conatiner" >
        
          <span class="kpi-title">{{ config.title }}</span>
          <span class="kpi-value">
            {{ config.prefix }}{{ displayValue }}{{ config.suffix }}
          </span>
          <span *ngIf="config.trend !== undefined"
                class="kpi-trend"
                [class.up]="config.trend >= 0"
                [class.down]="config.trend < 0">
            {{ config.trend >= 0 ? '↑' : '↓' }}
            {{ config.trend | number:'1.1-1' }}%
            <span *ngIf="config.trendLabel" class="trend-label">{{ config.trendLabel }}</span>
          </span>
        
      </div>

    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      container-type: size;
    }

    .kpi-card {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      display: flex;
      align-items: flex-start;
      gap: clamp(8px, 3cqw, 18px);
      padding: clamp(10px, 3cqh, 20px) clamp(10px, 3cqw, 20px);
      
      background-color: var(--dsh-primary, #fff);
      border-radius: 14px;
      border: 1.5px solid var(--dsh-accent, #fff);
      box-shadow: 0 2px 10px rgba(0,0,0,.06);
      overflow: hidden;
      transition: box-shadow .2s, transform .2s;
    }

    .kpi-card:hover {
      box-shadow: 0 6px 20px rgba(0,0,0,.1);
      transform: translateY(-2px);
    }

    /* Icon */
    .kpi-icon-wrap {
      flex-shrink: 0;
      width: clamp(90px, 8cqw, 52px);
      height: clamp(90px, 8cqw, 52px);
      border-radius: clamp(8px, 2cqw, 14px);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .kpi-icon-img {
      width: 60%;
      height: 60%;
      object-fit: contain;
    }

    .kpi-icon-fallback {
      font-size: clamp(14px, 4cqw, 22px);
      color: inherit;
      opacity: .7;
    }

    .kpi-text-conatiner{
      flex: 1;
      width: clamp(100px, 8cqw, 52px);
      height: clamp(150px, 8cqw, 52px);
      display:flex;
      flex-Direction:column;
      align-items:center;
      justify-content:center;
      gap: 2px;
    }
    /* Text column */
    .kpi-text {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .kpi-title {
      font-size: clamp(15px, 2.5cqw, 13px);
      font-weight: 500;
      // color: #5a6a85;
      color: var(--dsh-text, #0c0c0c);
      white-space: wrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }

    .kpi-value {
      font-size: clamp(20px, 4.5cqw, 26px);
      font-weight: 700;
      color: #2a3547;
      white-space: wrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.2;
      color: var(--dsh-text, #0c0c0c);
    }

    /* Trend badge */
    .kpi-trend {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: clamp(9px, 2cqw, 11px);
      font-weight: 600;
      padding: 2px clamp(4px, 1.5cqw, 8px);
      border-radius: 20px;
      width: fit-content;
      margin-top: 2px;
    }

    .kpi-trend.up   { background: #e8faf6; color: #0a8a6e; }
    .kpi-trend.down { background: #fef0ec; color: #c9543a; }
    .trend-label    { font-weight: 400; margin-left: 2px; }
  `]
})
export class KpiCardComponent implements OnChanges {
  @Input() config!: KpiCardConfig;

  displayValue = '';
  iconWrapStyles: Record<string, string> = {};

  ngOnInit(): void {
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.displayValue = this.formatValue(this.config.value);
      this.iconWrapStyles = {
        background: this.config.iconBackgroundColor || 'rgba(93,135,255,0.12)',
        color: this.config.iconColor || '#5d87ff',
      };
    }
  }

  private formatValue(v: number | string): string {
    if (typeof v === 'number') return v.toLocaleString();
    return v ?? '—';
  }
}